import { type ReactNode } from 'react'
import { cn } from '../../../lib/cn.ts'
import { SidebarLogo } from './SidebarLogo.tsx'
import { SidebarNav } from './SidebarNav.tsx'
import type { NavItem } from '../types.ts'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  /** Mobile (< md) off-canvas drawer open state. */
  mobileOpen: boolean
  items: NavItem[]
  brand?: ReactNode
  footer?: ReactNode
  resolvePath: (item: NavItem) => string
}

export function Sidebar({
  isCollapsed,
  onToggle,
  mobileOpen,
  items,
  brand,
  footer,
  resolvePath,
}: SidebarProps) {
  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col',
        'border-r border-border/60 bg-background',
        'transition-[transform,width] duration-300 ease-in-out',
        // Off-canvas drawer below md; the persistent rail returns at md+.
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
        // Mobile drawer is always full width; collapse is a desktop-only concept.
        'w-[var(--sidebar-width-expanded)]',
        isCollapsed
          ? 'md:w-[var(--sidebar-width-collapsed)]'
          : 'md:w-[var(--sidebar-width-expanded)]',
      )}
      aria-label="Primary"
    >
      <SidebarLogo isCollapsed={isCollapsed} onToggle={onToggle}>
        {brand}
      </SidebarLogo>

      <SidebarNav items={items} isCollapsed={isCollapsed} resolvePath={resolvePath} />

      {footer && <div className="px-3 pb-4">{footer}</div>}
    </aside>
  )
}
